import React, { useEffect, useMemo, useState } from "react";
import "./ProviderModal.css";
import ConfirmModal from "./ConfirmModal";

const parseListField = (text) =>
  (text || "")
    .split(/\n|\(\.\)/)
    .map((s) => s.trim())
    .filter(Boolean);

const formatHour = (h) => {
  if (h === null || h === undefined || h === "") return null;
  const s = String(h);
  return s.length >= 5 ? s.slice(0, 5) : s;
};

const ProviderDetailView = ({
  baseProvider,
  detail,
  loading,
  error,
  onBack,
  onAssign,
  isSelected,
}) => {
  const name = detail?.provider_name || baseProvider?.provider_name;
  const title = detail?.title || baseProvider?.title;
  const initials =
    (name || "")
      .split(" ")
      .map((w) => w[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "P";

  const opening = formatHour(detail?.opening_hour);
  const closing = formatHour(detail?.closing_hour);
  const hours =
    opening && closing
      ? `${opening} – ${closing}`
      : opening || closing || null;

  const address = [
    detail?.address,
    detail?.city || baseProvider?.city,
    detail?.province,
    detail?.country || baseProvider?.country,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <>
      <header className="pm-header pm-header--detail">
        <button
          type="button"
          className="pm-back"
          onClick={onBack}
          aria-label="Back to list"
        >
          <i className="bi bi-arrow-left" />
        </button>
        <div className="pm-header__text">
          <h2 className="pm-title">Provider Details</h2>
        </div>
      </header>

      <div className="pm-detail">
        <div className="pm-detail__hero">
          <div className="pm-detail__avatar">
            {baseProvider?.img_url ? (
              <img src={baseProvider.img_url} alt="" />
            ) : (
              <span>{initials}</span>
            )}
          </div>
          <div className="pm-detail__hero-text">
            <div className="pm-detail__name">
              {name}
              {detail?.verification === true && (
                <span
                  className="pm-detail__verified"
                  title="Verified provider"
                  aria-label="Verified provider"
                >
                  <i className="bi bi-patch-check-fill" />
                </span>
              )}
            </div>
            {title && <div className="pm-detail__title">{title}</div>}
          </div>
        </div>

        {loading && (
          <div className="pm-detail__loading">
            <div className="pm-spinner" aria-hidden="true" />
            <span>Loading details…</span>
          </div>
        )}

        {error && !loading && (
          <div className="pm-detail__error">
            <i className="bi bi-exclamation-triangle" />
            <span>{error}</span>
          </div>
        )}

        {!loading && !error && detail && (
          <>
            <section className="pm-section">
              <h3 className="pm-section__heading">Contact</h3>
              <div className="pm-fields">
                {detail.email && (
                  <div className="pm-field">
                    <i className="bi bi-envelope pm-field__icon" />
                    <div className="pm-field__body">
                      <div className="pm-field__label">Email</div>
                      <a className="pm-field__value pm-field__link" href={`mailto:${detail.email}`}>
                        {detail.email}
                      </a>
                    </div>
                  </div>
                )}
                {detail.phone_number && (
                  <div className="pm-field">
                    <i className="bi bi-telephone pm-field__icon" />
                    <div className="pm-field__body">
                      <div className="pm-field__label">Phone</div>
                      <a className="pm-field__value pm-field__link" href={`tel:${detail.phone_number}`}>
                        {detail.phone_number}
                      </a>
                    </div>
                  </div>
                )}
                {address && (
                  <div className="pm-field">
                    <i className="bi bi-geo-alt pm-field__icon" />
                    <div className="pm-field__body">
                      <div className="pm-field__label">Address</div>
                      <div className="pm-field__value">{address}</div>
                    </div>
                  </div>
                )}
              </div>
            </section>

            {(detail.experience || detail.year || detail.credentials) && (
              <section className="pm-section">
                <h3 className="pm-section__heading">Experience</h3>
                <div className="pm-fields">
                  {detail.experience && (
                    <div className="pm-field">
                      <i className="bi bi-briefcase pm-field__icon" />
                      <div className="pm-field__body">
                        <div className="pm-field__label">Experience</div>
                        <div className="pm-field__value">{detail.experience}</div>
                      </div>
                    </div>
                  )}
                  {detail.year && (
                    <div className="pm-field">
                      <i className="bi bi-calendar3 pm-field__icon" />
                      <div className="pm-field__body">
                        <div className="pm-field__label">Established</div>
                        <div className="pm-field__value">{detail.year}</div>
                      </div>
                    </div>
                  )}
                  {detail.credentials && (() => {
                    const items = parseListField(detail.credentials);
                    return (
                      <div className="pm-field pm-field--top">
                        <i className="bi bi-award pm-field__icon" />
                        <div className="pm-field__body">
                          <div className="pm-field__label">Credentials</div>
                          {items.length > 0 ? (
                            <ul className="pm-credential-list">
                              {items.map((item, idx) => (
                                <li key={idx}>{item}</li>
                              ))}
                            </ul>
                          ) : (
                            <div className="pm-field__value">—</div>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </section>
            )}

            {hours && (
              <section className="pm-section">
                <h3 className="pm-section__heading">Hours</h3>
                <div className="pm-fields">
                  <div className="pm-field">
                    <i className="bi bi-clock pm-field__icon" />
                    <div className="pm-field__body">
                      <div className="pm-field__label">Open</div>
                      <div className="pm-field__value">{hours}</div>
                    </div>
                  </div>
                </div>
              </section>
            )}

            {detail.self_description && (() => {
              const lines = parseListField(detail.self_description);
              return (
                <section className="pm-section">
                  <h3 className="pm-section__heading">About</h3>
                  {lines.length > 0 ? (
                    <div className="pm-bio">
                      {lines.map((line, idx) => (
                        <p key={idx}>{line}</p>
                      ))}
                    </div>
                  ) : (
                    <p className="pm-bio">{detail.self_description}</p>
                  )}
                </section>
              );
            })()}
          </>
        )}
      </div>

      <footer className="pm-footer">
        <button type="button" className="pm-btn pm-btn--ghost" onClick={onBack}>
          Back
        </button>
        <button
          type="button"
          className="pm-btn pm-btn--primary"
          onClick={onAssign}
          disabled={isSelected}
        >
          {isSelected ? (
            <>
              <i className="bi bi-check2" /> Already assigned
            </>
          ) : (
            <>
              <i className="bi bi-person-check" /> Assign provider
            </>
          )}
        </button>
      </footer>
    </>
  );
};

export const ProviderModal = ({
  show,
  onClose,
  providers = [],
  selectedProviderId,
  onSelect,
  bookingLabel,
  onFetchDetail,
}) => {
  const [search, setSearch] = useState("");
  const [view, setView] = useState("list");
  const [detailProvider, setDetailProvider] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [pendingProviderId, setPendingProviderId] = useState(null);

  useEffect(() => {
    if (!show) {
      setSearch("");
      setView("list");
      setDetailProvider(null);
      setDetail(null);
      setDetailError(null);
      return;
    }
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (view === "detail") setView("list");
        else onClose?.();
      }
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [show, onClose, view]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return providers;
    return providers.filter((p) => {
      const name = (p.provider_name || "").toLowerCase();
      const title = (p.title || "").toLowerCase();
      const city = (p.city || "").toLowerCase();
      const country = (p.country || "").toLowerCase();
      return (
        name.includes(q) ||
        title.includes(q) ||
        city.includes(q) ||
        country.includes(q)
      );
    });
  }, [providers, search]);

  const openDetail = async (provider) => {
    setDetailProvider(provider);
    setView("detail");
    setDetail(null);
    setDetailError(null);

    if (!onFetchDetail) {
      setDetail(provider);
      return;
    }

    setDetailLoading(true);
    try {
      const res = await onFetchDetail(provider.provider_id);
      const data = res?.data ?? res;
      setDetail(data || provider);
    } catch (err) {
      console.error(err);
      setDetailError("Failed to load provider details.");
      setDetail(provider);
    } finally {
      setDetailLoading(false);
    }
  };

  const backToList = () => {
    setView("list");
    setDetailProvider(null);
    setDetail(null);
    setDetailError(null);
  };

  if (!show) return null;

  const requestPick = (providerId) => setPendingProviderId(providerId);

  const confirmPick = () => {
    onSelect?.(pendingProviderId);
    setPendingProviderId(null);
    onClose?.();
  };

  const cancelPick = () => setPendingProviderId(null);

  const pendingProvider = providers.find((p) => p.provider_id === pendingProviderId);

  return (
    <>
    <div className="pm-overlay" role="dialog" aria-modal="true" aria-labelledby="pm-title">
      <div className="pm-backdrop" onClick={onClose} aria-hidden="true" />
      <div className="pm-dialog" role="document">
        {view === "detail" ? (
          <ProviderDetailView
            baseProvider={detailProvider}
            detail={detail}
            loading={detailLoading}
            error={detailError}
            onBack={backToList}
            onAssign={() =>
              detailProvider && requestPick(detailProvider.provider_id)
            }
            isSelected={detailProvider?.provider_id === selectedProviderId}
          />
        ) : (
          <>
            <header className="pm-header">
              <div className="pm-header__text">
                <h2 id="pm-title" className="pm-title">
                  <i className="bi bi-person-badge pm-title__icon" />
                  Assign Provider
                </h2>
                {bookingLabel && (
                  <p className="pm-subtitle">For booking {bookingLabel}</p>
                )}
              </div>
              <button
                type="button"
                className="pm-close"
                onClick={onClose}
                aria-label="Close dialog"
              >
                <i className="bi bi-x-lg" />
              </button>
            </header>

            <div className="pm-search">
              <i className="bi bi-search pm-search__icon" aria-hidden="true" />
              <input
                type="text"
                className="pm-search__input"
                placeholder="Search by name, title, or location…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
              />
              {search && (
                <button
                  type="button"
                  className="pm-search__clear"
                  onClick={() => setSearch("")}
                  aria-label="Clear search"
                >
                  <i className="bi bi-x" />
                </button>
              )}
            </div>

            <div className="pm-meta">
              <span className="pm-count">
                <strong>{filtered.length}</strong> of {providers.length} verified
              </span>
            </div>

            <div className="pm-list" role="listbox" aria-label="Available providers">
              {filtered.length === 0 ? (
                <div className="pm-empty">
                  <div className="pm-empty__icon">
                    <i className="bi bi-person-x" />
                  </div>
                  <h4>No providers match</h4>
                  <p>
                    {providers.length === 0
                      ? "No verified nearby providers for this booking."
                      : "Try a different search term."}
                  </p>
                </div>
              ) : (
                filtered.map((p) => {
                  const isSelected = p.provider_id === selectedProviderId;
                  const initials =
                    (p.provider_name || "")
                      .split(" ")
                      .map((w) => w[0])
                      .slice(0, 2)
                      .join("")
                      .toUpperCase() || "P";

                  return (
                    <div
                      key={p.provider_id}
                      role="option"
                      aria-selected={isSelected}
                      className={`pm-row ${isSelected ? "pm-row--selected" : ""}`}
                    >
                      <button
                        type="button"
                        className="pm-row__main"
                        onClick={() => requestPick(p.provider_id)}
                        aria-label={`Assign ${p.provider_name}`}
                      >
                        <div className="pm-row__avatar">
                          {p.img_url ? (
                            <img
                              src={p.img_url}
                              alt=""
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                              }}
                            />
                          ) : (
                            <span>{initials}</span>
                          )}
                        </div>
                        <div className="pm-row__body">
                          <div className="pm-row__name">
                            {p.provider_name}
                            {isSelected && (
                              <span className="pm-row__badge">Current</span>
                            )}
                          </div>
                          <div className="pm-row__meta">
                            <span className="pm-row__title">{p.title || "—"}</span>
                            {(p.city || p.country) && (
                              <>
                                <span className="pm-row__sep">·</span>
                                <span className="pm-row__loc">
                                  <i className="bi bi-geo-alt" />
                                  {[p.city, p.country].filter(Boolean).join(", ")}
                                </span>
                              </>
                            )}
                            {p.experience && (
                              <>
                                <span className="pm-row__sep">·</span>
                                <span className="pm-row__exp">
                                  <i className="bi bi-briefcase" />
                                  {p.experience}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </button>
                      <button
                        type="button"
                        className="pm-row__info"
                        onClick={() => openDetail(p)}
                        title="View details"
                        aria-label={`View details for ${p.provider_name}`}
                      >
                        <i className="bi bi-info-circle" />
                      </button>
                    </div>
                  );
                })
              )}
            </div>

            <footer className="pm-footer">
              <button type="button" className="pm-btn pm-btn--ghost" onClick={onClose}>
                Cancel
              </button>
            </footer>
          </>
        )}
      </div>
    </div>
    <ConfirmModal
      show={!!pendingProviderId}
      onConfirm={confirmPick}
      onCancel={cancelPick}
      title="Assign provider?"
      message={
        pendingProvider
          ? `Assign ${pendingProvider.provider_name} to this booking?`
          : undefined
      }
      confirmLabel="Assign"
      variant="accent"
    />
    </>
  );
};

export default ProviderModal;
